import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

/**
 * ori-share 線路測速的狀態。
 *
 * 刻意用 discriminated union 而非「一個字串欄位 + 空值代表失敗」：
 * 「還沒測」與「測了但沒有線路通過驗證」雖然都降級為隨機挑選，語意卻不同，
 * 混為一談會讓狀態無法觀測，也讓日後要區分兩者時無從下手。
 */
export type OriShareSpeedTest = { status: 'idle' } | { status: 'testing' } | { status: 'done'; fastest: string | null }

interface PreloadConfigState {
  loading: boolean
  /** 是否已達終局狀態（成功或失敗皆為 true），供等待方判斷是否還需要等 */
  loaded: boolean
  oriShare: string[]
  webShare: string[]
  error: string | null
  oriShareSpeedTest: OriShareSpeedTest
}

const initialState: PreloadConfigState = {
  loading: false,
  loaded: false,
  oriShare: [],
  webShare: [],
  error: null,
  oriShareSpeedTest: { status: 'idle' },
}

const preloadConfigSlice = createSlice({
  name: 'preloadConfig',
  initialState,
  reducers: {
    fetchPreloadConfig: (state) => {
      state.loading = true
      // 重抓時退出終局狀態，等待方才知道「還在進行中」而不是「已經定案」
      state.loaded = false
      state.error = null
    },
    setPreloadConfig: (state, action: PayloadAction<{ oriShare: string[]; webShare: string[] }>) => {
      state.loading = false
      state.loaded = true
      state.oriShare = action.payload.oriShare
      state.webShare = action.payload.webShare
    },
    /**
     * 測速 saga 的 trigger，同時是「一個 session 只測一次」的第一道保險。
     *
     * 只允許 idle → testing；已在測或已測完一律 no-op。
     * 不能只靠 watcher 的 takeLeading：shareVideo 在 oriShare 或 webShare 任一為空時
     * 每次分享都會重抓 preload（redux/saga/shareVideo.ts），成功後就會再次派發本 action，
     * 而前一個 worker 早已結束、takeLeading 並不會擋下它。
     */
    startOriShareSpeedTest: (state) => {
      if (state.oriShareSpeedTest.status !== 'idle') return
      state.oriShareSpeedTest = { status: 'testing' }
    },
    /** 測速終局：選出的網域，或全數未通過驗證時為 null */
    setOriShareSpeedTestResult: (state, action: PayloadAction<string | null>) => {
      state.oriShareSpeedTest = { status: 'done', fastest: action.payload }
    },
    /**
     * 背景刷新的 saga trigger。
     * 刻意不改動 loading / loaded —— 呼叫端仍可立即使用現有資料，
     * 刷新結果只透過 setPreloadConfig 在成功時覆寫。
     */
    refreshPreloadConfig: () => {},
    fetchPreloadConfigFailure: (state, action: PayloadAction<string | undefined>) => {
      state.loading = false
      state.loaded = true
      state.error = action.payload ?? null
    },
  },
})

export const preloadConfigActions = preloadConfigSlice.actions
export default preloadConfigSlice.reducer
